/* eslint-disable */

/**
 * Calculate APR as the **effective annual rate** (EAR) with 3-decimal percent output.
 * - P&I payment is computed from the note rate and rounded to cents.
 * - aprRelatedClosingCost are prepaid finance charges (reduce amount financed).
 * - paymentsPerYear defaults to 12 (monthly).
 */
export const calculateAPR = (
  loanAmount: number,
  interestRate: number,
  loanPeriodInYears: number,
  aprRelatedClosingCost: number = 0,
  paymentsPerYear = 12
): string => {
  const P = loanAmount;
  const ratePct = interestRate;
  const years = loanPeriodInYears;
  if (!Number.isFinite(P) || !Number.isFinite(ratePct) || !Number.isFinite(years) || P <= 0 || years <= 0) {
    return "0";
  }

  let fees = aprRelatedClosingCost;
  if (!Number.isFinite(fees) || fees < 0) fees = 0;
  if (fees >= P) return "0";

  // Amortization inputs
  const n = Math.max(1, Math.trunc(years * paymentsPerYear));
  const i_note = (ratePct / 100) / paymentsPerYear;

  // Payment at note rate on full principal (round to cents)
  let payment: number;
  if (i_note === 0) {
    payment = P / n;
  } else {
    const denom = 1 - Math.pow(1 + i_note, -n);
    if (!(denom > 0) || !Number.isFinite(denom)) return "0";
    payment = P * (i_note / denom);
  }
  payment = Math.round(payment * 100) / 100;
  if (!Number.isFinite(payment) || payment <= 0) return "0";

  // Amount financed (target PV)
  const targetPV = P - fees;

  // f(r) = PV(payments discounted at r) - targetPV
  const f = (r: number): number => {
    if (r === 0) return payment * n - targetPV;
    return payment * (1 - Math.pow(1 + r, -n)) / r - targetPV;
  };

  // Bisection solve for periodic APR r >= 0
  let lo = 0, hi = 1.0;
  let fLo = f(lo), fHi = f(hi);
  let tries = 0;
  while (fLo * fHi > 0 && tries < 20) {
    hi *= 2;
    fHi = f(hi);
    tries++;
  }
  if (fLo * fHi > 0) return "0";

  const tol = 1e-10, maxIter = 200;
  let mid = 0;
  for (let k = 0; k < maxIter; k++) {
    mid = (lo + hi) / 2;
    const fMid = f(mid);
    if (Math.abs(fMid) < 1e-12 || (hi - lo) < tol) break;
    if (fLo * fMid <= 0) { hi = mid; fHi = fMid; } else { lo = mid; fLo = fMid; }
  }

  const r_periodic = mid;
  if (!Number.isFinite(r_periodic) || r_periodic < 0) return "0";

  const aprEffectivePct = (Math.pow(1 + r_periodic, paymentsPerYear) - 1) * 100;

  return Number.isFinite(aprEffectivePct) ? aprEffectivePct.toFixed(3) : "0";
};
